import { useState, useCallback } from 'react';

const STORAGE_KEY = 'humble_machine_ratings';

export interface EpisodeRatingEntry {
  episodeId: number;
  episodeTitle: string;
  episodeNumber: number;
  rating: number;
  ratedAt: string;
}

export function useEpisodeRatings() {
  const [ratings, setRatings] = useState<Record<number, EpisodeRatingEntry>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const rateEpisode = useCallback(
    (episodeId: number, episodeTitle: string, episodeNumber: number, rating: number) => {
      setRatings((prev) => {
        const updated: Record<number, EpisodeRatingEntry> = {
          ...prev,
          [episodeId]: {
            episodeId,
            episodeTitle,
            episodeNumber,
            rating,
            ratedAt: new Date().toISOString(),
          },
        };
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        } catch {
          // ignore storage errors
        }
        return updated;
      });
    },
    []
  );

  const getRating = useCallback(
    (episodeId: number): number => ratings[episodeId]?.rating ?? 0,
    [ratings]
  );

  const allRatings = Object.values(ratings).sort(
    (a, b) => a.episodeNumber - b.episodeNumber
  );

  const averageRating =
    allRatings.length > 0
      ? allRatings.reduce((sum, r) => sum + r.rating, 0) / allRatings.length
      : 0;

  return { ratings, allRatings, rateEpisode, getRating, averageRating };
}
